import gsap from 'gsap';
import { Mesh, MeshBasicMaterial, Object3D, PlaneGeometry } from 'three';
import Image3D from './Image3D';
import happens from 'happens';
import IndexCover from './IndexCover';
import IndexThumbnail from './IndexThumbnail';
import IAsset from '../../interface/IAsset';
import planeGeometry from './geometry/plane';
import { getCurrentMinWidth } from '../../util/respondTo';
import { Breakpoint } from '../../data/styles/Breakpoint';
import scroller from '../../util/scroller';

export default class IndexPlane {
  private app: any;
  private images: Array<IAsset> = [];
  private thumbs = [];
  public mesh: Object3D;
  public hitarea: any;
  private cover: IndexCover;
  public x: number;
  public y: number;
  public radius: number;
  public resolution: number;
  public scale: number;
  private transitioned: boolean = false;
  public emit;
  public on;
  public loaded: boolean = false;
  private data: any;
  private index: any;

  constructor(data: any, app: any, index: number) {
    this.over = this.over.bind(this);
    this.out = this.out.bind(this);
    this.clicked = this.clicked.bind(this);
    this.resolution = getCurrentMinWidth() < Breakpoint.L ? 320 : 1920;
    this.radius = 3 * (window.innerWidth / this.resolution);
    this.app = app;
    this.data = data;
    this.index = index;
    this.images = data.images;
    this.mesh = new Object3D();
    this.cover = new IndexCover(data.images[0], this.app);
    this.addThumbs();
    this.addHitArea();
    this.mesh.scale.set(0.4,0.4,0.4)
    this.mesh.add(this.cover.mesh);
    happens(this);
  }

  public onScroll(scroll) {
    this.thumbs.map((thumb) => {
      thumb.onScroll(scroll);
    })
  }

  private addThumbs() {
    const angle = this.images.length / (Math.PI / 2)
    let total = Math.max(2, this.images.length);
    total = Math.min(4, total);
    this.images.map((img, i) => {
      if (i > 0 && i <= total) {
        const thumb = new IndexThumbnail(img, this.app, (angle * i), this.cover, this.index, -i * 0.1)
        thumb.mesh.position.z -= 0.1;
        this.mesh.add(thumb.mesh);
        this.thumbs.push(thumb);
      }
    })
  }

   private addHitArea() {
    const geo = planeGeometry(1,1);
    const mat = new MeshBasicMaterial({ color: 0x00FF00, transparent: true });
    mat.opacity = 0.0;
    this.hitarea = new Mesh(geo, mat);
    this.mesh.add(this.hitarea);
  }

  private scaleHitArea() {
    const width = this.cover.width;
    const height = this.cover.height;
    this.hitarea.scale.x = width * 1.5;
    this.hitarea.scale.y = height * 1.5;
    const xOffset = this.index % 2 === 0 ? (width / 2) - (this.cover.width / 4) : -(width / 2) + (this.cover.width / 4);
    this.hitarea.position.x = this.cover.mesh.position.x + xOffset;
    this.hitarea.position.y = -height/2

  }

  public dispose() {
    this.loaded = false;
    this.transitioned = false;
    this.mesh.scale.set(0.4,0.4,0.4)
    this.mesh.position.x = 0;
    this.mesh.position.y = 0;
    this.cover.dispose();
    this.thumbs.map((t) => t.dispose())
  }

  public preload(cb) {
    return this.cover.preload(() => {
      cb();
      this.loaded = true;
    })
  }

  private addEvents() {
    (this.hitarea as any).cursor = 'pointer';
    (this.hitarea as any).off('mouseover', this.over);
    (this.hitarea as any).on('mouseover', this.over);
    (this.hitarea as any).off('mouseout', this.out);
    (this.hitarea as any).on('mouseout', this.out);
    (this.hitarea as any).off('click', this.clicked);
    (this.hitarea as any).on('click', this.clicked);
    (this.hitarea as any).off('touchdown', this.clicked);
    (this.hitarea as any).on('touchdown', this.clicked);
    this.scaleHitArea();
  }

  private over() {
    this.emit('over', this.data)
    this.cover.overAnimation(this.x);
    this.thumbs.map((t, i) => {
      t.overAnimation(this.x, i / 20)
    })
  }

  private clicked() {
    this.emit('clicked', this.data)
  }

  private out() {
    this.emit('out')
    this.cover.outAnimation(0);
    this.thumbs.map((t, i) => {
      t.outAnimation(0)
    })
  }


  public update() {
    this.scaleHitArea();
    this.thumbs.map((t, i) => {
      t.update();
    })
    this.cover.update();
  }

  public resize(viewport) {
    if (this.transitioned) {
      this.mesh.position.x = this.x
      this.mesh.position.y = this.y
    }
    this.cover.resize(viewport);
    this.scaleHitArea();
  }

  public transitionIn() {
    const scale = { value: 0.4 }
    const tl = gsap.timeline({
      onStart: () => {
        this.transitioned = true;
        this.thumbs.map((t) => t.preload())
      },
      onComplete: () => {
        this.addEvents();
      }
    });
    tl.to(scale, { value: 1, onUpdate: () => {
      this.mesh.scale.set(scale.value,scale.value,scale.value)
    } }, 0);
    tl.add(this.cover.transitionIn(), 0)
    tl.fromTo(this.mesh.position, {x: 0, y: 0}, { duration: Math.max(1.5, Math.abs(this.y) / 6), ease: 'power4.out', x: this.x, y: this.y }, 0);
    return tl;
  }

  get height() {
    return this.cover.height;
  }
}
